import { gql } from '@apollo/client';

// GraphQL Query
export const GET_CONTACT_OTHERS = gql`
  query GetContactOthers($contactId: ID!) {
    node(id: $contactId) {
      ... on ContactNode {
        id
        name

        # Profile Settings (from profileIds One2many)
        profileIds {
          id
          confirmRemovePassword
          emailConfirmed
          localLoginDisabled
          lockoutEnabled
          loginEnabled
          mobilePhoneConfirmed
          twoFactorEnabled
          profileAlert
          profileIsAnonymous
        }

        socialMediaIds {
          id
          linkedin
          linkedinUrl
          onLinkedin
          twitter
          facebookUrl
          onFacebook
        }

        # Identifiers & System Info
        rowKey
        visitorKey
        importSequenceNumber
        process
        subscription
        autoCreated
        doNotShowInViewsJpOnly
        forceUpdateJpTest
        sqlDate
        soundex
        viewSuppress
        parentCustomerType

        # Names
        name1
        name2
        nameWithLp
        formName

        # Investment Related
        investorType
        potentialInvestorPriority
        targetClosingDate
        investmentCommittee
        coMingledEquityFund
        coMingledCreditFundIi
        coMingled
        partnersFundIi
        equitySma
        otherSmas
        principalProtectedNote
        syndicatedDirectEquityInvestments
        gpControlInvestor
        gpNonControlInvestor
        lpCredit
        lpEquity
        lpGeneric
        imaForSbicFundCoInvesting
        imaForSecondLienLoans
        imaForSeniorLoans

        # Relationships & Service Provider
        parentContactId {
          id
          name
        }
        relationshipPriority
        relationship1
        relationship2
        relationshipStrength
        relationshipStrengthDescription
        serviceProviderFlag
        serviceProviderType

        # Events & Memberships
        gccEntertainment
        businessEducationAndNetworkingEventsList
        nycMonthlyDinnerList
        hickeyAnnualHolidayCard
        jacksonHickeyBirthdayInvite
        ipiMember
        ipiOptInDate
        mcgillAlumnus
        metcircleMember

        # Personal Contact
        bhPersonalContact
        dobChild1
        dobChild2
        homePhone2Old
        pager
        previousEmployerAndTitle

        # System / QA
        ny2QaCheckNeeded
        ny2QaCheckedBy {
          id
          name
        }
        owningUser {
          id
          name
        }
        test1
        test2

        # LP Platform
        lpPlatformsCount
        lpPlatformsLastUpdated

        # Communication
        doNotSendMonthlyUpdates
        newZapRsvp
        crmUrl

        # Notes
        social
        nextStepsPlugin

        # India Research
        indiaResearchIds {
          id
          indiaContactOwnerFollowupDate
          indiaResearchDate
          lastIndia1QaCheckedBy {
            id
            name
          }
          lastIndia1QaDate
          india1QaCheckedBy {
            id
            name
          }
          india1QaCheckNeeded
          lastIndia2QaCheckedBy {
            id
            name
          }
          lastIndia2QaDate
          india2QaCheckedBy {
            id
            name
          }
          india2QaCheckNeeded
        }

        # PitchBook
        pitchbookIds {
          id
          pitchbookId
          pitchbookLink
          pbBiography
          pbLastSyncDate
          pbLastSyncUser {
            id
            name
          }
        }
      }
    }
  }
`;
// parent_contact_id

export interface ContactIndiaResearchData {
  india1QaCheckedBy: string;
  india1QaCheckedById: null | string;
  india1QaCheckNeeded: boolean;
  india2QaCheckedBy: string;
  india2QaCheckedById: null | string;
  india2QaCheckNeeded: boolean;
  indiaContactOwnerFollowupDate: string;
  indiaResearchDate: string;
  lastIndia1QaCheckedBy: string;
  lastIndia1QaCheckedById: null | string;
  lastIndia1QaDate: string;
  lastIndia2QaCheckedBy: string;
  lastIndia2QaCheckedById: null | string;
  lastIndia2QaDate: string;
}

export interface ContactIndiaResearchNode {
  id: string;
  india1QaCheckedBy?: { id: string; name: string } | null;
  india1QaCheckNeeded?: boolean | null;
  india2QaCheckedBy?: { id: string; name: string } | null;
  india2QaCheckNeeded?: boolean | null;
  indiaContactOwnerFollowupDate?: null | string;
  indiaResearchDate?: null | string;
  lastIndia1QaCheckedBy?: { id: string; name: string } | null;
  lastIndia1QaDate?: null | string;
  lastIndia2QaCheckedBy?: { id: string; name: string } | null;
  lastIndia2QaDate?: null | string;
}

// API Response Types
export interface ContactNodeWithOthers {
  autoCreated?: boolean | null;
  bhPersonalContact?: boolean | null;
  businessEducationAndNetworkingEventsList?: boolean | null;
  coMingled?: boolean | null;
  coMingledCreditFundIi?: boolean | null;
  coMingledEquityFund?: boolean | null;
  crmUrl?: null | string;
  dobChild1?: null | string;
  dobChild2?: null | string;
  doNotSendMonthlyUpdates?: boolean | null;
  doNotShowInViewsJpOnly?: boolean | null;
  equitySma?: boolean | null;
  forceUpdateJpTest?: null | string;
  formName?: null | string;
  gccEntertainment?: null | string;
  gpControlInvestor?: boolean | null;
  gpNonControlInvestor?: boolean | null;
  hickeyAnnualHolidayCard?: boolean | null;
  homePhone2Old?: null | string;
  id: string;
  imaForSbicFundCoInvesting?: boolean | null;
  imaForSecondLienLoans?: boolean | null;
  imaForSeniorLoans?: boolean | null;
  importSequenceNumber?: null | number;
  indiaResearchIds?:
    | ContactIndiaResearchNode
    | ContactIndiaResearchNode[]
    | null;
  investmentCommittee?: boolean | null;
  investorType?: null | string;
  ipiMember?: boolean | null;
  ipiOptInDate?: null | string;
  jacksonHickeyBirthdayInvite?: boolean | null;
  lpCredit?: boolean | null;
  lpEquity?: boolean | null;
  lpGeneric?: boolean | null;
  lpPlatformsCount?: null | number;
  lpPlatformsLastUpdated?: null | string;
  mcgillAlumnus?: boolean | null;
  metcircleMember?: boolean | null;
  name: string;
  name1?: null | string;
  name2?: null | string;
  nameWithLp?: null | string;
  newZapRsvp?: null | string;
  nextStepsPlugin?: null | string;
  ny2QaCheckedBy?: { id: string; name: string } | null;
  ny2QaCheckNeeded?: boolean | null;
  nycMonthlyDinnerList?: boolean | null;
  otherSmas?: boolean | null;
  owningUser?: { id: string; name: string } | null;
  pager?: null | string;
  parentContactId?: { id: string; name: string } | null;
  parentCustomerType?: null | string;
  partnersFundIi?: boolean | null;
  pitchbookIds?: ContactPitchbookNode | ContactPitchbookNode[] | null;
  potentialInvestorPriority?: null | string;
  previousEmployerAndTitle?: null | string;
  principalProtectedNote?: boolean | null;
  process?: null | string;
  profileIds?: null | ProfileNode | ProfileNode[];
  relationship1?: null | string;
  relationship2?: null | string;
  relationshipPriority?: null | string;
  relationshipStrength?: null | number;
  relationshipStrengthDescription?: null | string;
  rowKey?: null | string;
  serviceProviderFlag?: boolean | null;
  serviceProviderType?: null | string;
  social?: null | string;
  socialMediaIds?: null | SocialMediaNode | SocialMediaNode[];
  soundex?: null | string;
  sqlDate?: null | string;
  subscription?: null | string;
  syndicatedDirectEquityInvestments?: boolean | null;
  targetClosingDate?: null | string;
  test1?: boolean | null;
  test2?: boolean | null;
  viewSuppress?: boolean | null;
  visitorKey?: null | string;
}

// UI Data Types
export interface ContactOtherFieldsData {
  autoCreated: boolean;
  bhPersonalContact: boolean;
  businessEducationAndNetworkingEventsList: boolean;
  coMingled: boolean;
  coMingledCreditFundIi: boolean;
  coMingledEquityFund: boolean;
  crmUrl: string;
  dobChild1: string;
  dobChild2: string;
  doNotSendMonthlyUpdates: boolean;
  doNotShowInViewsJpOnly: boolean;
  equitySma: boolean;
  forceUpdateJpTest: string;
  formName: string;
  gccEntertainment: string;
  gpControlInvestor: boolean;
  gpNonControlInvestor: boolean;
  hickeyAnnualHolidayCard: boolean;
  homePhone2Old: string;
  imaForSbicFundCoInvesting: boolean;
  imaForSecondLienLoans: boolean;
  imaForSeniorLoans: boolean;
  importSequenceNumber: null | number;
  investmentCommittee: boolean;
  investorType: string;
  ipiMember: boolean;
  ipiOptInDate: string;
  jacksonHickeyBirthdayInvite: boolean;
  lpCredit: boolean;
  lpEquity: boolean;
  lpGeneric: boolean;
  mcgillAlumnus: boolean;
  metCircleMember: boolean;
  name1: string;
  name2: string;
  nameWithLp: string;
  newZapRsvp: string;
  nextStepsPlugin: string;
  numberOfLpPlatforms: null | number;
  numberOfLpPlatformsLastUpdatedOn: string;
  ny2QaCheckedBy: string;
  ny2QaCheckedById: null | string;
  ny2QaCheckNeeded: boolean;
  nycMonthlyDinnerList: boolean;
  otherSmas: boolean;
  owningUser: string;
  owningUserId: null | string;
  pager: string;
  parentContactId: string;
  parentContactIdId: null | string;
  parentCustomerType: string;
  partnersFundIi: boolean;
  potentialInvestorPriority: string;
  previousEmployerAndTitle: string;
  principalProtectedNote: boolean;
  process: string;
  relationship1: string;
  relationship2: string;
  relationshipPriority: string;
  relationshipStrength: null | number;
  relationshipStrengthDescription: string;
  rowKey: string;
  serviceProvider: boolean;
  serviceProviderType: string;
  social: string;
  soundEx: string;
  sqlDate: string;
  subscription: string;
  syndicatedDirectEquityInvestments: boolean;
  targetClosingDate: string;
  test1: boolean;
  test2: boolean;
  viewSuppress: boolean;
  visitorKey: string;
}

export interface ContactOthersData {
  indiaResearch: ContactIndiaResearchData;
  otherFields: ContactOtherFieldsData;
  pitchbook: ContactPitchbookData;
  profile: ContactProfileData;
  socialMedia: ContactSocialMediaData;
}

export interface ContactPitchbookData {
  pbBiography: string;
  pbLastSyncDate: string;
  pbLastSyncUser: string;
  pitchbookId: string;
  pitchbookLink: string;
}

export interface ContactPitchbookNode {
  id: string;
  pbBiography?: null | string;
  pbLastSyncDate?: null | string;
  pbLastSyncUser?: { id: string; name: string } | null;
  pitchbookId?: null | string;
  pitchbookLink?: null | string;
}

export interface ContactProfileData {
  confirmRemovePassword: boolean;
  emailConfirmed: boolean;
  localLoginDisabled: boolean;
  lockoutEnabled: boolean;
  loginEnabled: boolean;
  mobilePhoneConfirmed: boolean;
  profileAlert: boolean;
  profileIsAnonymous: boolean;
  twoFactorEnabled: boolean;
}

export interface ContactSocialMediaData {
  facebook: string;
  facebookUrl: string;
  linkedin: string;
  linkedinUrl: string;
  onFacebook: boolean;
  onLinkedin: boolean;
  twitter: string;
}

export interface ProfileNode {
  confirmRemovePassword?: boolean | null;
  emailConfirmed?: boolean | null;
  id: string;
  localLoginDisabled?: boolean | null;
  lockoutEnabled?: boolean | null;
  loginEnabled?: boolean | null;
  mobilePhoneConfirmed?: boolean | null;
  profileAlert?: boolean | null;
  profileIsAnonymous?: boolean | null;
  twoFactorEnabled?: boolean | null;
}

export interface SocialMediaNode {
  facebookUrl?: null | string;
  id: string;
  linkedin?: null | string;
  linkedinUrl?: null | string;
  onFacebook?: boolean | null;
  onLinkedin?: boolean | null;
  twitter?: null | string;
}

export function createEmptyContactOthersData(): ContactOthersData {
  return {
    indiaResearch: {
      india1QaCheckedBy: '',
      india1QaCheckedById: null,
      india1QaCheckNeeded: false,
      india2QaCheckedBy: '',
      india2QaCheckedById: null,
      india2QaCheckNeeded: false,
      indiaContactOwnerFollowupDate: '',
      indiaResearchDate: '',
      lastIndia1QaCheckedBy: '',
      lastIndia1QaCheckedById: null,
      lastIndia1QaDate: '',
      lastIndia2QaCheckedBy: '',
      lastIndia2QaCheckedById: null,
      lastIndia2QaDate: ''
    },
    otherFields: {
      autoCreated: false,
      bhPersonalContact: false,
      businessEducationAndNetworkingEventsList: false,
      coMingled: false,
      coMingledCreditFundIi: false,
      coMingledEquityFund: false,
      crmUrl: '',
      dobChild1: '',
      dobChild2: '',
      doNotSendMonthlyUpdates: false,
      doNotShowInViewsJpOnly: false,
      equitySma: false,
      forceUpdateJpTest: '',
      formName: '',
      gccEntertainment: '',
      gpControlInvestor: false,
      gpNonControlInvestor: false,
      hickeyAnnualHolidayCard: false,
      homePhone2Old: '',
      imaForSbicFundCoInvesting: false,
      imaForSecondLienLoans: false,
      imaForSeniorLoans: false,
      importSequenceNumber: null,
      investmentCommittee: false,
      investorType: '',
      ipiMember: false,
      ipiOptInDate: '',
      jacksonHickeyBirthdayInvite: false,
      lpCredit: false,
      lpEquity: false,
      lpGeneric: false,
      mcgillAlumnus: false,
      metCircleMember: false,
      name1: '',
      name2: '',
      nameWithLp: '',
      newZapRsvp: '',
      nextStepsPlugin: '',
      numberOfLpPlatforms: null,
      numberOfLpPlatformsLastUpdatedOn: '',
      ny2QaCheckedBy: '',
      ny2QaCheckedById: null,
      ny2QaCheckNeeded: false,
      nycMonthlyDinnerList: false,
      otherSmas: false,
      owningUser: '',
      owningUserId: null,
      pager: '',
      parentContactId: '',
      parentContactIdId: null,
      parentCustomerType: '',
      partnersFundIi: false,
      potentialInvestorPriority: '',
      previousEmployerAndTitle: '',
      principalProtectedNote: false,
      process: '',
      relationship1: '',
      relationship2: '',
      relationshipPriority: '',
      relationshipStrength: null,
      relationshipStrengthDescription: '',
      rowKey: '',
      serviceProvider: false,
      serviceProviderType: '',
      social: '',
      soundEx: '',
      sqlDate: '',
      subscription: '',
      syndicatedDirectEquityInvestments: false,
      targetClosingDate: '',
      test1: false,
      test2: false,
      viewSuppress: false,
      visitorKey: ''
    },
    pitchbook: {
      pbBiography: '',
      pbLastSyncDate: '',
      pbLastSyncUser: '',
      pitchbookId: '',
      pitchbookLink: ''
    },
    profile: {
      confirmRemovePassword: false,
      emailConfirmed: false,
      localLoginDisabled: false,
      lockoutEnabled: false,
      loginEnabled: false,
      mobilePhoneConfirmed: false,
      profileAlert: false,
      profileIsAnonymous: false,
      twoFactorEnabled: false
    },
    socialMedia: {
      facebook: '',
      facebookUrl: '',
      linkedin: '',
      linkedinUrl: '',
      onFacebook: false,
      onLinkedin: false,
      twitter: ''
    }
  };
}

export function mapContactOthers(
  node: ContactNodeWithOthers
): ContactOthersData {
  // Handle profileIds - can be single object or array
  let profileNode: null | ProfileNode = null;
  if (node.profileIds) {
    if (Array.isArray(node.profileIds)) {
      profileNode = node.profileIds[0] ?? null;
    } else {
      profileNode = node.profileIds;
    }
  }

  // Handle socialMediaIds - can be single object or array
  let socialMediaNode: null | SocialMediaNode = null;
  if (node.socialMediaIds) {
    if (Array.isArray(node.socialMediaIds)) {
      socialMediaNode = node.socialMediaIds[0] ?? null;
    } else {
      socialMediaNode = node.socialMediaIds;
    }
  }

  // Handle indiaResearchIds - can be single object or array
  let indiaResearchNode: ContactIndiaResearchNode | null = null;
  if (node.indiaResearchIds) {
    if (Array.isArray(node.indiaResearchIds)) {
      indiaResearchNode = node.indiaResearchIds[0] ?? null;
    } else {
      indiaResearchNode = node.indiaResearchIds;
    }
  }

  // Handle pitchbookIds - can be single object or array
  let pitchbookNode: ContactPitchbookNode | null = null;
  if (node.pitchbookIds) {
    if (Array.isArray(node.pitchbookIds)) {
      pitchbookNode = node.pitchbookIds[0] ?? null;
    } else {
      pitchbookNode = node.pitchbookIds;
    }
  }

  return {
    indiaResearch: {
      india1QaCheckedBy: indiaResearchNode?.india1QaCheckedBy?.name ?? '',
      india1QaCheckedById: indiaResearchNode?.india1QaCheckedBy?.id ?? null,
      india1QaCheckNeeded: indiaResearchNode?.india1QaCheckNeeded ?? false,
      india2QaCheckedBy: indiaResearchNode?.india2QaCheckedBy?.name ?? '',
      india2QaCheckedById: indiaResearchNode?.india2QaCheckedBy?.id ?? null,
      india2QaCheckNeeded: indiaResearchNode?.india2QaCheckNeeded ?? false,
      indiaContactOwnerFollowupDate:
        indiaResearchNode?.indiaContactOwnerFollowupDate ?? '',
      indiaResearchDate: indiaResearchNode?.indiaResearchDate ?? '',
      lastIndia1QaCheckedBy:
        indiaResearchNode?.lastIndia1QaCheckedBy?.name ?? '',
      lastIndia1QaCheckedById:
        indiaResearchNode?.lastIndia1QaCheckedBy?.id ?? null,
      lastIndia1QaDate: indiaResearchNode?.lastIndia1QaDate ?? '',
      lastIndia2QaCheckedBy:
        indiaResearchNode?.lastIndia2QaCheckedBy?.name ?? '',
      lastIndia2QaCheckedById:
        indiaResearchNode?.lastIndia2QaCheckedBy?.id ?? null,
      lastIndia2QaDate: indiaResearchNode?.lastIndia2QaDate ?? ''
    },
    otherFields: {
      autoCreated: node.autoCreated ?? false,
      bhPersonalContact: node.bhPersonalContact ?? false,
      businessEducationAndNetworkingEventsList:
        node.businessEducationAndNetworkingEventsList ?? false,
      coMingled: node.coMingled ?? false,
      coMingledCreditFundIi: node.coMingledCreditFundIi ?? false,
      coMingledEquityFund: node.coMingledEquityFund ?? false,
      crmUrl: node.crmUrl ?? '',
      dobChild1: node.dobChild1 ?? '',
      dobChild2: node.dobChild2 ?? '',
      doNotSendMonthlyUpdates: node.doNotSendMonthlyUpdates ?? false,
      doNotShowInViewsJpOnly: node.doNotShowInViewsJpOnly ?? false,
      equitySma: node.equitySma ?? false,
      forceUpdateJpTest: node.forceUpdateJpTest ?? '',
      formName: node.formName ?? '',
      gccEntertainment: node.gccEntertainment ?? '',
      gpControlInvestor: node.gpControlInvestor ?? false,
      gpNonControlInvestor: node.gpNonControlInvestor ?? false,
      hickeyAnnualHolidayCard: node.hickeyAnnualHolidayCard ?? false,
      homePhone2Old: node.homePhone2Old ?? '',
      imaForSbicFundCoInvesting: node.imaForSbicFundCoInvesting ?? false,
      imaForSecondLienLoans: node.imaForSecondLienLoans ?? false,
      imaForSeniorLoans: node.imaForSeniorLoans ?? false,
      importSequenceNumber: node.importSequenceNumber ?? null,
      investmentCommittee: node.investmentCommittee ?? false,
      investorType: node.investorType ?? '',
      ipiMember: node.ipiMember ?? false,
      ipiOptInDate: node.ipiOptInDate ?? '',
      jacksonHickeyBirthdayInvite: node.jacksonHickeyBirthdayInvite ?? false,
      lpCredit: node.lpCredit ?? false,
      lpEquity: node.lpEquity ?? false,
      lpGeneric: node.lpGeneric ?? false,
      mcgillAlumnus: node.mcgillAlumnus ?? false,
      metCircleMember: node.metcircleMember ?? false,
      name1: node.name1 ?? '',
      name2: node.name2 ?? '',
      nameWithLp: node.nameWithLp ?? '',
      newZapRsvp: node.newZapRsvp ?? '',
      nextStepsPlugin: node.nextStepsPlugin ?? '',
      numberOfLpPlatforms: node.lpPlatformsCount ?? null,
      numberOfLpPlatformsLastUpdatedOn: node.lpPlatformsLastUpdated ?? '',
      ny2QaCheckedBy: node.ny2QaCheckedBy?.name ?? '',
      ny2QaCheckedById: node.ny2QaCheckedBy?.id ?? null,
      ny2QaCheckNeeded: node.ny2QaCheckNeeded ?? false,
      nycMonthlyDinnerList: node.nycMonthlyDinnerList ?? false,
      otherSmas: node.otherSmas ?? false,
      owningUser: node.owningUser?.name ?? '',
      owningUserId: node.owningUser?.id ?? null,
      pager: node.pager ?? '',
      parentContactId: node.parentContactId?.name ?? '',
      parentContactIdId: node.parentContactId?.id ?? null,
      parentCustomerType: node.parentCustomerType ?? '',
      partnersFundIi: node.partnersFundIi ?? false,
      potentialInvestorPriority: node.potentialInvestorPriority ?? '',
      previousEmployerAndTitle: node.previousEmployerAndTitle ?? '',
      principalProtectedNote: node.principalProtectedNote ?? false,
      process: node.process ?? '',
      relationship1: node.relationship1 ?? '',
      relationship2: node.relationship2 ?? '',
      relationshipPriority: node.relationshipPriority ?? '',
      relationshipStrength: node.relationshipStrength ?? null,
      relationshipStrengthDescription:
        node.relationshipStrengthDescription ?? '',
      rowKey: node.rowKey ?? '',
      serviceProvider: node.serviceProviderFlag ?? false,
      serviceProviderType: node.serviceProviderType ?? '',
      social: node.social ?? '',
      soundEx: node.soundex ?? '',
      sqlDate: node.sqlDate ?? '',
      subscription: node.subscription ?? '',
      syndicatedDirectEquityInvestments:
        node.syndicatedDirectEquityInvestments ?? false,
      targetClosingDate: node.targetClosingDate ?? '',
      test1: node.test1 ?? false,
      test2: node.test2 ?? false,
      viewSuppress: node.viewSuppress ?? false,
      visitorKey: node.visitorKey ?? ''
    },
    pitchbook: {
      pbBiography: pitchbookNode?.pbBiography ?? '',
      pbLastSyncDate: pitchbookNode?.pbLastSyncDate ?? '',
      pbLastSyncUser: pitchbookNode?.pbLastSyncUser?.name ?? '',
      pitchbookId: pitchbookNode?.pitchbookId ?? '',
      pitchbookLink: pitchbookNode?.pitchbookLink ?? ''
    },
    profile: mapProfile(profileNode),
    socialMedia: mapSocialMedia(socialMediaNode)
  };
}

function mapProfile(node: null | ProfileNode | undefined): ContactProfileData {
  if (!node) {
    return {
      confirmRemovePassword: false,
      emailConfirmed: false,
      localLoginDisabled: false,
      lockoutEnabled: false,
      loginEnabled: false,
      mobilePhoneConfirmed: false,
      profileAlert: false,
      profileIsAnonymous: false,
      twoFactorEnabled: false
    };
  }
  return {
    confirmRemovePassword: node.confirmRemovePassword ?? false,
    emailConfirmed: node.emailConfirmed ?? false,
    localLoginDisabled: node.localLoginDisabled ?? false,
    lockoutEnabled: node.lockoutEnabled ?? false,
    loginEnabled: node.loginEnabled ?? false,
    mobilePhoneConfirmed: node.mobilePhoneConfirmed ?? false,
    profileAlert: node.profileAlert ?? false,
    profileIsAnonymous: node.profileIsAnonymous ?? false,
    twoFactorEnabled: node.twoFactorEnabled ?? false
  };
}

function mapSocialMedia(
  node: null | SocialMediaNode | undefined
): ContactSocialMediaData {
  if (!node) {
    return {
      facebook: '',
      facebookUrl: '',
      linkedin: '',
      linkedinUrl: '',
      onFacebook: false,
      onLinkedin: false,
      twitter: ''
    };
  }
  return {
    facebook: '', // Not in query, using empty string
    facebookUrl: node.facebookUrl ?? '',
    linkedin: node.linkedin ?? '',
    linkedinUrl: node.linkedinUrl ?? '',
    onFacebook: node.onFacebook ?? false,
    onLinkedin: node.onLinkedin ?? false,
    twitter: node.twitter ?? ''
  };
}

// Options for choice fields
export const forceUpdateJpTestOptions = [
  { label: 'Option A', value: 'Option A' },
  { label: 'Option B', value: 'Option B' },
  { label: 'Option C', value: 'Option C' }
];

export const gccEntertainmentOptions = [
  { label: 'Concert', value: 'Concert' },
  { label: 'Dinner', value: 'Dinner' },
  { label: 'Golf', value: 'Golf' },
  { label: 'Sports Event', value: 'Sports Event' },
  { label: 'Theater', value: 'Theater' }
];

export const investorTypeOptions = [
  { label: 'Family Office', value: 'Family Office' },
  { label: 'Fund of Funds', value: 'Fund of Funds' },
  { label: 'Individual', value: 'Individual' },
  { label: 'Institutional', value: 'Institutional' }
];

export const potentialInvestorPriorityOptions = [
  { label: 'High', value: 'High' },
  { label: 'Low', value: 'Low' },
  { label: 'Medium', value: 'Medium' }
];

export const relationshipOptions = [
  { label: 'Primary', value: 'Primary' },
  { label: 'Secondary', value: 'Secondary' },
  { label: 'Tertiary', value: 'Tertiary' }
];

export const relationshipPriorityOptions = [
  { label: 'High', value: 'High' },
  { label: 'Low', value: 'Low' },
  { label: 'Medium', value: 'Medium' }
];

export const serviceProviderTypeOptions = [
  { label: 'Accounting', value: 'Accounting' },
  { label: 'Consulting', value: 'Consulting' },
  { label: 'Legal', value: 'Legal' },
  { label: 'Technology', value: 'Technology' }
];

export const targetClosingDateOptions = [
  { label: 'Q1 2025', value: 'Q1 2025' },
  { label: 'Q2 2025', value: 'Q2 2025' },
  { label: 'Q3 2025', value: 'Q3 2025' },
  { label: 'Q4 2025', value: 'Q4 2025' }
];

export const yesNoOptions = [
  { label: 'No', value: 'No' },
  { label: 'Yes', value: 'Yes' }
];
